import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Palette, Save, RotateCcw, Type, Layout, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FONTS = [
  { value: 'Playfair Display', label: 'Playfair Display (Elegant Serif)' },
  { value: 'Merriweather', label: 'Merriweather (Classic Serif)' },
  { value: 'Lora', label: 'Lora (Modern Serif)' },
  { value: 'Cormorant Garamond', label: 'Cormorant Garamond (Refined Serif)' },
  { value: 'Inter', label: 'Inter (Clean Sans)' },
  { value: 'Poppins', label: 'Poppins (Geometric Sans)' },
  { value: 'Open Sans', label: 'Open Sans (Friendly Sans)' },
  { value: 'Roboto', label: 'Roboto (Modern Sans)' },
];

const DEFAULT_THEME = {
  primary_color: '#722F37',
  secondary_color: '#C9A962',
  accent_color: '#5D4E6D',
  background_color: '#FAF8F5',
  text_color: '#2D2D2D',
  heading_font: 'Playfair Display',
  body_font: 'Inter',
  navbar_style: 'floating',
  footer_style: 'full',
  button_style: 'rounded',
  card_style: 'elevated',
  enable_animations: true,
  enable_grain_overlay: true
};

export default function AdminTheme() {
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load Google Fonts dynamically for preview
  useEffect(() => {
    const fontFamilies = FONTS.map(f => f.value.replace(/ /g, '+')).join('&family=');
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamilies}:wght@400;500;600;700&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      const response = await axios.get(`${API}/theme`, { withCredentials: true });
      if (response.data) {
        setTheme({ ...DEFAULT_THEME, ...response.data });
      }
    } catch (error) {
      console.error('Failed to load theme');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/theme`, theme, { withCredentials: true });
      toast.success('Theme saved! Refresh the page to see changes.');
    } catch (error) {
      toast.error('Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset to default theme?')) {
      setTheme(DEFAULT_THEME);
      toast.info('Theme reset to defaults. Click Save to apply.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-theme-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif text-charcoal">Theme Editor</h1>
          <p className="text-charcoal/60">Customize your site's appearance</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleReset}
            variant="outline"
            className="border-gold/20"
          >
            <RotateCcw size={18} className="mr-2" /> Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-burgundy hover:bg-purple text-white"
          >
            <Save size={18} className="mr-2" /> {saving ? 'Saving...' : 'Save Theme'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Colors */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 border border-gold/10"
        >
          <div className="flex items-center gap-2 mb-6">
            <Palette size={20} className="text-burgundy" />
            <h3 className="font-serif text-xl text-charcoal">Colors</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label>Primary Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.primary_color}
                  onChange={(e) => setTheme({...theme, primary_color: e.target.value})}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={theme.primary_color}
                  onChange={(e) => setTheme({...theme, primary_color: e.target.value})}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-charcoal/50 mt-1">Main brand color (buttons, headers)</p>
            </div>

            <div>
              <Label>Secondary Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.secondary_color}
                  onChange={(e) => setTheme({...theme, secondary_color: e.target.value})}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={theme.secondary_color}
                  onChange={(e) => setTheme({...theme, secondary_color: e.target.value})}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-charcoal/50 mt-1">Accent highlights</p>
            </div>

            <div>
              <Label>Accent Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.accent_color}
                  onChange={(e) => setTheme({...theme, accent_color: e.target.value})}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={theme.accent_color}
                  onChange={(e) => setTheme({...theme, accent_color: e.target.value})}
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <Label>Background Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.background_color}
                  onChange={(e) => setTheme({...theme, background_color: e.target.value})}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={theme.background_color}
                  onChange={(e) => setTheme({...theme, background_color: e.target.value})}
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <Label>Text Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.text_color}
                  onChange={(e) => setTheme({...theme, text_color: e.target.value})}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={theme.text_color}
                  onChange={(e) => setTheme({...theme, text_color: e.target.value})}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Typography */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 border border-gold/10"
        >
          <div className="flex items-center gap-2 mb-6">
            <Type size={20} className="text-burgundy" />
            <h3 className="font-serif text-xl text-charcoal">Typography</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label>Heading Font</Label>
              <Select value={theme.heading_font} onValueChange={(v) => setTheme({...theme, heading_font: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONTS.map(font => (
                    <SelectItem key={font.value} value={font.value}>{font.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-charcoal/50 mt-1">Used for titles and headings</p>
            </div>

            <div>
              <Label>Body Font</Label>
              <Select value={theme.body_font} onValueChange={(v) => setTheme({...theme, body_font: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONTS.map(font => (
                    <SelectItem key={font.value} value={font.value}>{font.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-charcoal/50 mt-1">Used for paragraphs and body text</p>
            </div>

            {/* Font Preview */}
            <div className="mt-6 p-4 bg-cream rounded-lg">
              <p className="text-xs text-charcoal/50 mb-2">Preview:</p>
              <h4 style={{ fontFamily: theme.heading_font }} className="text-2xl text-charcoal mb-2">
                Heading Example
              </h4>
              <p style={{ fontFamily: theme.body_font }} className="text-charcoal/70">
                This is how your body text will look. The quick brown fox jumps over the lazy dog.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Layout & Effects */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 border border-gold/10"
        >
          <div className="flex items-center gap-2 mb-6">
            <Layout size={20} className="text-burgundy" />
            <h3 className="font-serif text-xl text-charcoal">Layout & Effects</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label>Navbar Style</Label>
              <Select value={theme.navbar_style} onValueChange={(v) => setTheme({...theme, navbar_style: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="floating">Floating (Centered)</SelectItem>
                  <SelectItem value="fixed">Fixed (Full Width)</SelectItem>
                  <SelectItem value="minimal">Minimal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Footer Style</Label>
              <Select value={theme.footer_style} onValueChange={(v) => setTheme({...theme, footer_style: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full (Multi-column)</SelectItem>
                  <SelectItem value="minimal">Minimal</SelectItem>
                  <SelectItem value="centered">Centered</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Button Style</Label>
              <Select value={theme.button_style} onValueChange={(v) => setTheme({...theme, button_style: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rounded">Rounded</SelectItem>
                  <SelectItem value="pill">Pill</SelectItem>
                  <SelectItem value="square">Square</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Card Style</Label>
              <Select value={theme.card_style} onValueChange={(v) => setTheme({...theme, card_style: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="elevated">Elevated (Shadow)</SelectItem>
                  <SelectItem value="flat">Flat</SelectItem>
                  <SelectItem value="bordered">Bordered</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="pt-4 border-t border-gold/10 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Animations</Label>
                  <p className="text-xs text-charcoal/50">Page transitions & effects</p>
                </div>
                <Switch
                  checked={theme.enable_animations}
                  onCheckedChange={(checked) => setTheme({...theme, enable_animations: checked})}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Grain Overlay</Label>
                  <p className="text-xs text-charcoal/50">Subtle texture effect</p>
                </div>
                <Switch
                  checked={theme.enable_grain_overlay}
                  onCheckedChange={(checked) => setTheme({...theme, enable_grain_overlay: checked})}
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Live Preview */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-6 bg-white rounded-xl p-6 border border-gold/10"
      >
        <div className="flex items-center gap-2 mb-6">
          <Sparkles size={20} className="text-burgundy" />
          <h3 className="font-serif text-xl text-charcoal">Color Preview</h3>
        </div>
        
        <div 
          className="rounded-xl p-8"
          style={{ backgroundColor: theme.background_color }}
        >
          <div className="flex flex-wrap gap-4 mb-6">
            <div 
              className="w-20 h-20 rounded-lg shadow-md flex items-center justify-center text-white text-xs"
              style={{ backgroundColor: theme.primary_color }}
            >
              Primary
            </div>
            <div 
              className="w-20 h-20 rounded-lg shadow-md flex items-center justify-center text-charcoal text-xs"
              style={{ backgroundColor: theme.secondary_color }}
            >
              Secondary
            </div>
            <div 
              className="w-20 h-20 rounded-lg shadow-md flex items-center justify-center text-white text-xs"
              style={{ backgroundColor: theme.accent_color }}
            >
              Accent
            </div>
          </div>
          
          <h4 
            style={{ fontFamily: theme.heading_font, color: theme.text_color }}
            className="text-2xl mb-2"
          >
            Sample Heading
          </h4>
          <p 
            style={{ fontFamily: theme.body_font, color: theme.text_color }}
            className="opacity-70 mb-4"
          >
            This is sample body text showing your typography choices.
          </p>
          
          <div className="flex gap-2">
            <button
              style={{ 
                backgroundColor: theme.primary_color,
                borderRadius: theme.button_style === 'pill' ? '9999px' : theme.button_style === 'square' ? '0' : '8px'
              }}
              className="px-4 py-2 text-white text-sm"
            >
              Primary Button
            </button>
            <button
              style={{ 
                backgroundColor: theme.secondary_color,
                borderRadius: theme.button_style === 'pill' ? '9999px' : theme.button_style === 'square' ? '0' : '8px'
              }}
              className="px-4 py-2 text-charcoal text-sm"
            >
              Secondary Button
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
